import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, map as rxMap } from 'rxjs/operators';
import { StatoSessioneService } from '../../../../shared/services/stato-sessione.service';
import { TestWithMetadata } from '../../test/test-id.service';
import { DynamicRefinedTestCase } from '../../../../shared/templateGen/templateInterfaces';
import { MicroScenarioSpecificationModel } from '../three-column-workspace/three-column-workspace.component';

export interface RequirementItem {
  id: string; // This will now be the stable, unique ID
  displayId: string; // This will be the sequential number like "1", "2"
  category: string;
  description: string;
  fullId: string;
  covered: boolean;
  relatedMicroScenarioIds: string[];
}

export interface CategoryWithCount {
  name: string;
  count: number;
}

@Component({
  selector: 'app-trace-matrix',
  templateUrl: './trace-matrix.component.html',
  styleUrls: ['./trace-matrix.component.scss']
})
export class TraceMatrixComponent implements OnInit, OnDestroy {
  private analysisTextInternal: string | null = null;
  private destroy$ = new Subject<void>();
  requirements: RequirementItem[] = [];
  filteredRequirements: RequirementItem[] = [];
  categoriesWithCounts: CategoryWithCount[] = [];
  currentFilter: string | null = null;
  private allRawTests: TestWithMetadata[] = [];
  private allConsolidatedTests: DynamicRefinedTestCase[] = [];
  allMicroSpecs: MicroScenarioSpecificationModel[] = [];
  microScenariosGenerated = false;

  // Store original constraints to sync IDs
  private extractedConstraints: any[] = [];

  isRtmVisible = false; // Controls visibility of the RTM table/filters
  kpiTotalRequirements = 0;
  kpiCovered = 0;
  kpiNotCovered = 0;

  coverageFilterState: 'all' | 'covered' | 'not-covered' = 'all';

  testsForExpandedRow: TestWithMetadata[] = [];
  // Proprietà per la riga espansa
  expandedRequirementId: string | null = null;
  rawTestsForExpandedRow: TestWithMetadata[] = [];
  consolidatedTestsForExpandedRow: TestWithMetadata[] = [];
  // Add properties to hold the full test datasets from the service
  private allStandardTests: TestWithMetadata[] = [];

  // Properties for inline editing and adding
  editingReqId: string | null = null; // Tracks the ID of the requirement being edited
  isAddingNew = false; // Flag to show the 'add new' row
  // Holds the data for the item being edited or added
  editData: { category: string; description: string } = { category: 'General', description: '' };

  constructor(
    private statoSessioneService: StatoSessioneService,
    private cd: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    combineLatest([
      this.statoSessioneService.requirementsAnalysis$,
      this.statoSessioneService.microScenarioSpecifications$,
      this.statoSessioneService.generatedTests$, // 1. Legge i test grezzi
      this.statoSessioneService.globalConsolidatedTests$, // 2. NUOVO: Legge i test consolidati
      this.statoSessioneService.extractedConstraints$
    ]).pipe(
      takeUntil(this.destroy$),
      rxMap(([requirementsAnalysis, microSpecsMap, rawTests, consolidatedTests, constraints]) => {

        // Save constraints locally
        this.extractedConstraints = constraints || [];

        // ... (logica esistente per analysisText e microSpecs)
        if (requirementsAnalysis && requirementsAnalysis.scenario_analysis) {
          this.analysisTextInternal = requirementsAnalysis.scenario_analysis;
        } else {
          this.analysisTextInternal = null;
        }
        this.parseAnalysisText();

        this.allMicroSpecs = [];
        if (microSpecsMap && microSpecsMap.size > 0) {
          microSpecsMap.forEach(macroState => {
            if (macroState.specifications) {
              this.allMicroSpecs.push(...macroState.specifications);
            }
          });
        }
        this.microScenariosGenerated = this.allMicroSpecs.length > 0;

        // MODIFICA: Salva entrambi i set di test
        this.allRawTests = rawTests || [];
        this.allConsolidatedTests = consolidatedTests || [];

        this.updateCoverageData();

        return null;
      })
    ).subscribe(() => {
      // Se una riga era aperta, ricarica i suoi dati in caso di aggiornamenti
      if (this.expandedRequirementId) {
        const req = this.requirements.find(r => r.fullId === this.expandedRequirementId);
        if (req) this.loadTestsForRequirement(req);
      }
      this.cd.detectChanges();
    });
  }

  private parseAnalysisText(): void {
    if (!this.analysisTextInternal) {
      this.requirements = [];
    } else {
      const lines = this.analysisTextInternal.split('\n').filter(line => line.trim() !== '');
      this.requirements = lines.map((line, index) => {
        // Generate a unique, stable ID. Using a simple timestamp + random for this example.
        const uniqueId = `req-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        const match = line.match(/^(?:\d+\.\s*)?\[([^\]]+)\]\s*(.*)$/);
        if (match) {
          const category = match[1].trim();
          const description = match[2].trim();
          return {
            id: uniqueId,
            displayId: `${index + 1}`,
            category: category,
            description: description,
            fullId: `${category}-${index + 1}`,
            covered: false,
            relatedMicroScenarioIds: []
          };
        }
        return {
          id: uniqueId,
          displayId: `${index + 1}`,
          category: 'General',
          description: line.trim(),
          fullId: `GEN-${index + 1}`,
          covered: false,
          relatedMicroScenarioIds: []
        };
      }).filter(req => req.description);
    }

    this.updateCategoriesAndCounts();
    // Sync constraint IDs after parsing new requirements
    this.syncConstraintIds();
  }

  /**
   * Syncs the IDs of extractedConstraints with the displayIds of the parsed requirements.
   * This ensures that the constraints used in the LLM prompt (which come from extractedConstraints)
   * match the IDs used in the Micro Scenarios (which use displayIds from here).
   */
  private syncConstraintIds(): void {
    if (this.extractedConstraints.length === 0 || this.requirements.length === 0) {
      return;
    }

    let updated = false;
    const requirementsMap = new Map<string, string>(); // Description -> DisplayId

    // Create a normalized map of descriptions to IDs
    this.requirements.forEach(req => {
      requirementsMap.set(req.description.trim(), req.displayId);
      // Also handle potential slight variations if needed, but strict trim match is a good start
    });

    const updatedConstraints = this.extractedConstraints.map(constraint => {
      const displayId = requirementsMap.get(constraint.description.trim());
      if (displayId && constraint.id !== displayId) {
        console.log(`[TraceMatrix] Syncing ID for constraint: "${constraint.description.substring(0, 20)}..." from ${constraint.id} to ${displayId}`);
        updated = true;
        return { ...constraint, id: displayId };
      }
      return constraint;
    });

    if (updated) {
      this.statoSessioneService.updateExtractedConstraints(updatedConstraints);
    }
  }

  private updateCoverageData(): void {
    if (this.requirements.length === 0) {
      this.filteredRequirements = [];
      this.calculateKpis(0);
      this.statoSessioneService.updateProcessedRequirements([]);
      return;
    }

    let coveredCount = 0;
    this.requirements.forEach(req => {
      req.covered = false;
      req.relatedMicroScenarioIds = [];
      if (this.microScenariosGenerated) {
        this.allMicroSpecs.forEach(microSpec => {
          if (microSpec.covered_requirements && Array.isArray(microSpec.covered_requirements)) {
            // Use displayId for matching, as this corresponds to the "1", "2", etc.
            if (microSpec.covered_requirements.includes(req.displayId)) {
              req.covered = true;
              if (!req.relatedMicroScenarioIds.includes(microSpec.id)) {
                req.relatedMicroScenarioIds.push(microSpec.id);
              }
            }
          }
        });
        if (req.relatedMicroScenarioIds.length > 0) {
          req.relatedMicroScenarioIds.sort();
        }
      }

      if (req.covered) { coveredCount++; }
    });
    this.calculateKpis(coveredCount);
    this.updateFilteredRequirements();
    this.updateCategoriesAndCounts(); // Recalculate categories after any potential changes
    this.statoSessioneService.updateProcessedRequirements(this.requirements);
  }

  private updateCategoriesAndCounts(): void {
    const categoryMap = new Map<string, number>();
    this.requirements.forEach(req => {
      categoryMap.set(req.category, (categoryMap.get(req.category) || 0) + 1);
    });
    this.categoriesWithCounts = Array.from(categoryMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  applyFilter(category: string | null): void {
    this.currentFilter = category;
    this.coverageFilterState = 'all';
    this.updateFilteredRequirements();
  }

  private updateFilteredRequirements(): void {
    let tempRequirements = [...this.requirements];
    if (this.coverageFilterState === 'covered') {
      tempRequirements = tempRequirements.filter(req => req.covered);
    } else if (this.coverageFilterState === 'not-covered') {
      tempRequirements = tempRequirements.filter(req => !req.covered);
    }
    if (this.currentFilter !== null) {
      tempRequirements = tempRequirements.filter(req => req.category === this.currentFilter);
    }
    this.filteredRequirements = tempRequirements;
    this.cd.detectChanges();
  }

  getCategoryCount(category: string): number {
    const foundCategory = this.categoriesWithCounts.find(c => c.name === category);
    return foundCategory ? foundCategory.count : 0;
  }

  getTotalRequirementsCount(): number {
    return this.requirements.length;
  }

  trackByReqId(index: number, item: RequirementItem): string {
    return item.id;
  }

  private calculateKpis(coveredCount: number): void {
    this.kpiTotalRequirements = this.requirements.length;
    this.kpiCovered = coveredCount;
    this.kpiNotCovered = this.kpiTotalRequirements - this.kpiCovered;
  }

  applyCoverageFilter(status: 'all' | 'covered' | 'not-covered'): void {
    this.coverageFilterState = status;
    // When a coverage filter is applied, reset the category filter for a clean UI.
    this.currentFilter = null;
    this.updateFilteredRequirements();
  }

  /**
   * Toggles the expanded/collapsed state of a requirement row.
   */
  toggleRow(requirement: RequirementItem): void {
    if (this.expandedRequirementId === requirement.fullId) {
      this.expandedRequirementId = null;
      this.rawTestsForExpandedRow = [];
      this.consolidatedTestsForExpandedRow = [];
    } else {
      this.expandedRequirementId = requirement.fullId;
      this.loadTestsForRequirement(requirement);
    }
  }

  /**
   * Finds and loads the standard and refined tests for the currently expanded requirement.
   */
  private loadTestsForRequirement(requirement: RequirementItem): void {
    console.log('=== Loading tests for requirement:', requirement.fullId, '===');
    console.log('Requirement micro scenarios:', requirement.relatedMicroScenarioIds);

    // 1. Load raw tests (existing logic)
    const relatedMicroIds = new Set(requirement.relatedMicroScenarioIds);
    this.rawTestsForExpandedRow = this.allRawTests.filter(test =>
      test.metadata?.microScenarioId && relatedMicroIds.has(test.metadata.microScenarioId)
    );

    console.log('Raw tests found:', this.rawTestsForExpandedRow.length);

    // 2. NEW: Load consolidated tests by micro scenario overlap
    this.consolidatedTestsForExpandedRow = this.allConsolidatedTests
      .filter(consolidatedTest => {
        // Check if this consolidated test covers any of the micro scenarios for this requirement
        if (consolidatedTest.sourceMicroScenarioIds && consolidatedTest.sourceMicroScenarioIds.length > 0) {
          const hasOverlap = consolidatedTest.sourceMicroScenarioIds.some(microId =>
            relatedMicroIds.has(microId)
          );

          if (hasOverlap) {
            console.log(`Consolidated test ${consolidatedTest.id} matches - covers micro scenarios:`,
              consolidatedTest.sourceMicroScenarioIds.filter(id => relatedMicroIds.has(id)));
            return true;
          }
        }
        return false;
      })
      .map((consolidatedTest, index) => ({
        metadata: {
          displayId: consolidatedTest.id,
          testId: consolidatedTest.id,
          sequentialId: index + 1,
          source: 'ConsolidatedTrace',
          gherkinSection: 'when' as const,
          testIndex: index
        },
        data: {
          ...consolidatedTest.data,
          /* Add helpful metadata for display
          _sourceMicroScenarioIds: consolidatedTest.sourceMicroScenarioIds,
          _sourceTestCount: consolidatedTest.sourceTestIds?.length || 0,
          _consolidationRationale: consolidatedTest.rationale,
          _overlappingMicroScenarios: consolidatedTest.sourceMicroScenarioIds?.filter(id => relatedMicroIds.has(id)) || []*/
        }
      }));

    console.log('Consolidated tests found:', this.consolidatedTestsForExpandedRow.length);
    console.log('=== End loading tests ===');
  }

  /**
   * Calculates the number of columns for the expanded row's colspan attribute.
   */
  getColspan(): number {
    let count = 3; // Req ID, Description, Actions
    if (this.currentFilter === null) { count++; } // Category column
    if (this.microScenariosGenerated) { count += 2; } // Covered & Related Micro Scenarios
    return count;
  }

  private updateAndPropagateChanges(): void {
    // 1. Rebuild the analysis text with proper line numbering
    this.analysisTextInternal = this.requirements
      .map((req, index) => `${index + 1}. [${req.category}] ${req.description}`)
      .join('\n');

    // 2. Re-sequence displayId and fullId
    this.requirements.forEach((req, index) => {
      req.displayId = `${index + 1}`;
      req.fullId = `${req.category}-${req.displayId}`;
    });

    // 3. Update the service. This will trigger the `ngOnInit` observable chain.
    // NOTE: You need a method on your service like `updateRequirementsAnalysisText`
    this.statoSessioneService.updateRequirementsAnalysisText(this.analysisTextInternal);

    // 4. Reset editing state
    this.editingReqId = null;
    this.isAddingNew = false;
    this.editData = { category: 'General', description: '' };
  }

  // ++ ADD: Handler to start editing a requirement
  onEdit(req: RequirementItem): void {
    this.editingReqId = req.id;
    this.editData = { category: req.category, description: req.description };
    this.isAddingNew = false; // Ensure 'add new' mode is off
  }

  // ++ ADD: Handler to save an edited requirement
  onSave(): void {
    if (!this.editingReqId) return;

    const reqToUpdate = this.requirements.find(r => r.id === this.editingReqId);
    if (reqToUpdate) {
      reqToUpdate.category = this.editData.category;
      reqToUpdate.description = this.editData.description;
    }
    this.updateAndPropagateChanges();
  }

  // ++ ADD: Handler to cancel editing
  onCancel(): void {
    this.editingReqId = null;
    this.isAddingNew = false;
  }

  // ++ ADD: Handler to delete a requirement
  onDelete(reqIdToDelete: string): void {
    if (confirm('Are you sure you want to delete this requirement? This action cannot be undone.')) {
      this.requirements = this.requirements.filter(r => r.id !== reqIdToDelete);
      this.updateAndPropagateChanges();
    }
  }

  // ++ ADD: Handler to start adding a new requirement
  onAddNew(): void {
    this.isAddingNew = true;
    this.editData = { category: 'General', description: '' };
    this.editingReqId = null; // Ensure 'edit' mode is off
  }

  // ++ ADD: Handler to save the new requirement
  onSaveNew(): void {
    if (!this.editData.description.trim()) {
      alert('Description cannot be empty.');
      return;
    }
    const uniqueId = `req-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const newReq: RequirementItem = {
      id: uniqueId,
      displayId: '', // Will be set in updateAndPropagateChanges
      category: this.editData.category.trim() || 'General',
      description: this.editData.description.trim(),
      fullId: '', // Will be set in updateAndPropagateChanges
      covered: false,
      relatedMicroScenarioIds: []
    };
    this.requirements.push(newReq);
    this.updateAndPropagateChanges();
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
